import { useEffect, useRef, useState } from 'react';
import SwitchInput from '../../../components/SwitchInput/SwitchInput';
import { resetUserPassword, saveUser } from '../../../services/UsersService';
import SelectPlan from './SelectPlan';

/**
 * props:
 * - data
 * - onSubmit
 */
function UserModal(props) {
    const DEFAULT_USER = {
        name: '',
        email: '',
        password: '',
        planId: 0,
        telegramChat: '',
        phone: '',
        trialDays: '',
        isActive: false,
    };

    const [error, setError] = useState('');

    const [user, setUser] = useState(DEFAULT_USER);
    const [selectedPlan, setSelectedPlan] = useState(null);

    const btnClose = useRef('');
    const btnSave = useRef('');

    function onSubmit(event) {
        if (!user.name?.trim()) {
            setError('Name is required');
            return;
        }
        if (!user.email?.trim()) {
            setError('Email is required');
            return;
        }
        if (!user.planId) {
            setError('Plan is required');
            return;
        }

        const isTrial = !!selectedPlan?.isTrial;
        const trialDays = String(user.trialDays ?? '').trim();
        if (isTrial && trialDays && !/^[1-9]\d*$/.test(trialDays)) {
            setError('Trial days must be an integer greater than zero');
            return;
        }

        const payload = { ...user, trialDays: isTrial ? trialDays : '' };

        saveUser(user.id, payload)
            .then(result => {
                btnClose.current.click();
                if (props.onSubmit) props.onSubmit(result);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? err.response.data : err.message);
            });
    }

    function onResetClick(event) {
        resetUserPassword(user.id)
            .then(result => {
                btnClose.current.click();
                if (props.onSubmit) props.onSubmit(result);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? err.response.data : err.message);
            });
    }

    function onInputChange(event) {
        setUser(prevState => ({
            ...prevState,
            [event.target.id]: event.target.value,
        }));
    }

    useEffect(() => {
        setUser(props.data);
        setError('');
    }, [props.data.id]);

    // The reset on close leaves the form empty while props.data keeps the same
    // id, so the effect above does not run when the same user is reopened.
    const dataRef = useRef(props.data);
    dataRef.current = props.data;

    useEffect(() => {
        const modal = document.getElementById('modalUser');
        modal.addEventListener('hidden.bs.modal', event => {
            setUser({ ...DEFAULT_USER });
        });
        modal.addEventListener('show.bs.modal', event => {
            setUser(dataRef.current);
            setError('');
        });
    }, []);

    return (
        <div
            className="modal fade"
            id="modalUser"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="modalTitleNotify"
            aria-hidden="true"
        >
            <div
                className="modal-dialog modal-dialog-centered modal-lg"
                role="document"
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="modalTitleNotify">
                            {props.data.id ? 'Edit ' : 'New '}User
                        </p>
                        <button
                            ref={btnClose}
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="close"
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className="form-group">
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="name">
                                            Name:{' '}
                                            <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={user.name || ''}
                                            placeholder="User's full name"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="email">
                                            Email:{' '}
                                            <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="email"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={user.email || ''}
                                            placeholder="user@email.com"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="phone">
                                            Cellphone:
                                        </label>
                                        <input
                                            type="text"
                                            id="phone"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={user.phone || ''}
                                            placeholder="+5551123456789"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="telegramChat">
                                            Telegram Chat ID:{' '}
                                            <a
                                                className="badge bg-secondary py-1"
                                                href="/telegram-chat"
                                                target="_blank"
                                                tabIndex={-1}
                                            >
                                                ?
                                            </a>
                                        </label>
                                        <input
                                            type="text"
                                            id="telegramChat"
                                            className="form-control"
                                            onChange={onInputChange}
                                            value={user.telegramChat || ''}
                                            placeholder="123456789"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="plan">
                                            Plan:{' '}
                                            <span className="required">*</span>
                                        </label>
                                        <SelectPlan
                                            id={user.planId}
                                            onChange={onInputChange}
                                            onPlanChange={setSelectedPlan}
                                            required
                                        />
                                    </div>
                                </div>
                                {selectedPlan?.isTrial ? (
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group">
                                            <label htmlFor="trialDays">
                                                {user.id > 0
                                                    ? 'Restart trial (days):'
                                                    : 'Trial days:'}
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                step="1"
                                                id="trialDays"
                                                className="form-control"
                                                onChange={onInputChange}
                                                value={user.trialDays ?? ''}
                                                placeholder="Server default"
                                            />
                                            <small className="text-muted">
                                                {user.id > 0 &&
                                                user.trialExpiresAt
                                                    ? `Expires at ${new Date(
                                                          user.trialExpiresAt,
                                                      ).toLocaleString()}. Leave empty to keep it.`
                                                    : 'Leave empty to use the server default.'}
                                            </small>
                                        </div>
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <SwitchInput
                                            id="isActive"
                                            text="Is Active?"
                                            onChange={onInputChange}
                                            isChecked={user.isActive || false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        {error ? (
                            <div className="alert alert-danger mt-1 col-9 py-1">
                                {error}
                            </div>
                        ) : (
                            <></>
                        )}
                        {user.id > 0 ? (
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={onResetClick}
                                title="resetUserPassword -> sendResetAlerts"
                            >
                                Reset Password
                            </button>
                        ) : (
                            <></>
                        )}
                        <button
                            ref={btnSave}
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={onSubmit}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UserModal;
